/**
 * The filter history in localStorage: one masked item holding, per
 * table scope (`<accountUuid>:<tableKey>`), the last filters the user
 * settled on.
 *
 * Lives one session. `clearFilterHistory` runs on every sign-out and
 * every sign-in (userSlice), and `sweepFilterHistoryIfSignedOut` when
 * the dashboard opens without a session — so a browser closed without
 * logging out holds it only until the dashboard is opened again.
 *
 * Every access is guarded: storage can be full, blocked or disabled,
 * and the history is a convenience that must never break a table.
 */
import { addEntry } from './rules';
import { mask, maskKey, unmask } from './mask';

export const STORAGE_KEY = 'qhs.filterHistory';

const isSignedIn = () => {
  try {
    return localStorage.getItem('isLogin') === 'true';
  } catch (error) {
    return false;
  }
};

export const clearFilterHistory = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    // Nothing stored that we could reach.
  }
};

export const sweepFilterHistoryIfSignedOut = () => {
  if (!isSignedIn()) clearFilterHistory();
};

// { owner, tables: { [scope]: entries } } for this user; anything else
// (another user's history, an older build's, a damaged item) is dropped.
const read = userUuid => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { owner: userUuid, tables: {} };
    const data = unmask(raw, maskKey(userUuid));
    if (data?.owner === userUuid && data.tables && typeof data.tables === 'object') return data;
  } catch (error) {
    // Fall through to a fresh history.
  }
  clearFilterHistory();
  return { owner: userUuid, tables: {} };
};

export const getEntries = (userUuid, scope) => {
  if (!userUuid || !scope) return [];
  const entries = read(userUuid).tables[scope];
  return Array.isArray(entries) ? entries : [];
};

/**
 * Records `filter` for the table. Refused once the session is over: a
 * table unmounting during sign-out must not write the history back
 * after it was cleared.
 */
export const recordFilter = (userUuid, scope, filter) => {
  if (!userUuid || !scope || !isSignedIn()) return;
  const data = read(userUuid);
  const current = data.tables[scope] || [];
  const next = addEntry(current, filter);
  if (next === current) return;
  data.tables[scope] = next;
  try {
    localStorage.setItem(STORAGE_KEY, mask(data, maskKey(userUuid)));
  } catch (error) {
    // Storage full or blocked: the history simply does not grow.
  }
};
